/**
 * A TELEPÍTÉS-SÁV (#57) — „tedd ki a kezdőképernyőre".
 *
 * MŰVELET, NEM OLDAL (`CLAUDE.md` §2): egy kis sáv a munkaképernyőn, ami
 * eltűnik, amint nincs rá szükség. Három eset, és csak kettőben szól:
 *
 *   • A BÖNGÉSZŐ FELKÍNÁLJA (Android és asztali Chrome, Edge): egy gomb, ami a
 *     böngésző saját telepítő-párbeszédét nyitja.
 *   • iPHONE / iPAD, böngészőből: ott nincs ilyen gomb és nem is lesz — az
 *     Apple nem ad rá eseményt. A sáv elmondja az utat: Megosztás → Főképernyőhöz
 *     adás.
 *   • MÁR TELEPÍTVE (a kezdőképernyőről indult) vagy egyik sem: hallgat.
 *
 * BEZÁRHATÓ, és a bezárást megjegyzi. Egy sáv, ami minden megnyitáskor újra
 * felbukkan, nyaggatás — és a „Most nem" után az ügyfél joggal várja, hogy
 * ne kérdezzük újra. A tároló hibáját (privát mód, tiltott tár) elnyeljük: a
 * legrosszabb eset, hogy a sáv újra megjelenik.
 */

import { useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { getInstallState, promptInstall, subscribe } from './installPrompt';

/** A bezárás emléke. Verziózott, hogy egy későbbi, más szövegű sáv újra szólhasson. */
const DISMISSED_KEY = 'fakt.install-banner.dismissed.v1';

export function InstallBanner() {
  const { t } = useTranslation();
  const install = useSyncExternalStore(subscribe, getInstallState);
  const [dismissed, setDismissed] = useState(readDismissed);

  if (dismissed || install.installed || isStandalone()) {
    return null;
  }

  const dismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  // A böngésző saját ablakában mondott „Mégsem" is „most nem": enélkül a
  // Chrome a következő betöltéskor újra küldi az eseményt, és a sáv visszajön.
  const startInstall = async () => {
    if (!(await promptInstall())) {
      dismiss();
    }
  };

  if (install.canPrompt) {
    return (
      <section className="install-bar" aria-label={t('install.label')}>
        <p>{t('install.prompt')}</p>
        <div className="install-actions">
          <button type="button" className="pick pick-primary" onClick={() => void startInstall()}>
            {t('install.action')}
          </button>
          <button type="button" className="pick pick-link" onClick={dismiss}>
            {t('install.notNow')}
          </button>
        </div>
      </section>
    );
  }

  if (isIos()) {
    return (
      <section className="install-bar" aria-label={t('install.label')}>
        <p>{t('install.iosHint')}</p>
        <div className="install-actions">
          <button type="button" className="pick pick-link" onClick={dismiss}>
            {t('install.gotIt')}
          </button>
        </div>
      </section>
    );
  }

  return null;
}

/** A kezdőképernyőről indult — a Safari a `navigator.standalone`-t adja, a többi a média-lekérdezést. */
function isStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

/**
 * iPhone vagy iPad. Az iPadOS 13 óta asztali Macnek mondja magát — onnan az
 * érintőpontok száma különbözteti meg.
 */
function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // Tiltott tár: a sáv a következő megnyitáskor újra megjelenik. Elviselhető.
  }
}
